import { Link, Outlet, useLocation } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useCurrentUser } from "@/api/hooks";
import { logo } from "@/assets";
import { ProgressBar } from "@/components/ui/progress-bar";
import { OnboardingSelectionBoundary } from "./onboarding-selection-boundary";

const STEP_PATHS = [
  "/onboarding/sign-up",
  "/onboarding/verify-phone",
  "/onboarding/business",
  "/onboarding/services",
  "/onboarding/team-size",
  "/onboarding/location",
] as const;

const TOTAL_STEPS = STEP_PATHS.length;

const BUSINESS_FIRST_STEP_PATHS = [
  "/onboarding/business",
  "/onboarding/services",
  "/onboarding/team-size",
  "/onboarding/location",
] as const;

const BUSINESS_FIRST_TOTAL_STEPS = BUSINESS_FIRST_STEP_PATHS.length;

function getProgressValue(pathname: string, businessFirst = false): number {
  if (businessFirst) {
    const normalizedPathname = pathname.startsWith("/onboarding/new")
      ? "/onboarding/business"
      : pathname;
    const idx = BUSINESS_FIRST_STEP_PATHS.findIndex((p) => normalizedPathname.startsWith(p));
    if (idx !== -1) return Math.round(((idx + 1) / BUSINESS_FIRST_TOTAL_STEPS) * 100);
  }

  const idx = STEP_PATHS.findIndex((p) => pathname.startsWith(p));
  if (idx === -1) return 100;
  return Math.round(((idx + 1) / TOTAL_STEPS) * 100);
}

export function getPreviousStepPath(pathname: string, businessFirst = false): string | null {
  if (
    businessFirst &&
    (pathname.startsWith("/onboarding/new") || pathname.startsWith("/onboarding/business"))
  ) {
    return "/dashboard";
  }

  if (pathname.startsWith("/onboarding/verify-phone")) return "/";

  const idx = STEP_PATHS.findIndex((p) => pathname.startsWith(p));
  if (idx <= 0) return null;
  return STEP_PATHS[idx - 1] ?? null;
}

function OnboardingLayout() {
  const { pathname } = useLocation();
  const { data: user } = useCurrentUser();
  const businessFirst = pathname.startsWith("/onboarding/new") || !!user?.has_business;
  const progress = getProgressValue(pathname, businessFirst);
  const isComplete = pathname.startsWith("/onboarding/complete");
  const isSelecting = pathname === "/onboarding/select-business";
  const needsBusiness = ["services", "team-size", "location", "complete"].some(
    (step) => pathname === `/onboarding/${step}`,
  );
  const previousStep = getPreviousStepPath(pathname, businessFirst);

  return (
    <div className="flex min-h-[var(--visual-vh,100vh)] flex-col bg-paper">
      <header className="sticky top-0 z-10 bg-paper">
        <div className="mx-auto flex max-w-[480px] items-center gap-2.5 px-5 py-3.5">
          {previousStep && !isComplete ? (
            <Link
              to={previousStep}
              className="-ml-1 flex size-8 items-center justify-center rounded-full text-slate transition-colors hover:bg-fog hover:text-ink"
              aria-label="Go to previous step"
            >
              <ArrowLeft className="size-4" />
            </Link>
          ) : (
            <img src={logo} alt="" className="block size-8 object-contain" />
          )}
          <span className="font-display text-[18px] font-semibold tracking-[-0.015em] text-ink">
            Slotique
          </span>
        </div>
        {!isComplete && !isSelecting && (
          <ProgressBar value={progress} className="mx-auto max-w-[480px]" />
        )}
      </header>

      <main className="mx-auto w-full max-w-[480px] flex-1 px-5 pt-8 pb-16">
        {needsBusiness ? (
          <OnboardingSelectionBoundary>
            <Outlet />
          </OnboardingSelectionBoundary>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}

export { OnboardingLayout };
